import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkle, Copy, ArrowClockwise, ArrowLeft, PaperPlaneTilt, FacebookLogo, InstagramLogo, WhatsappLogo, ChatCircle, FileText } from "@phosphor-icons/react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { fpPath } from "@/lib/site";
import FarmersNav from "@/components/FarmersNav";
import SEOHead from "@/components/SEOHead";

const TONES = [
  { key: "warm", label: "Warm" },
  { key: "professional", label: "Professional" },
  { key: "playful", label: "Playful" },
  { key: "urgent", label: "Urgent" },
  { key: "premium", label: "Premium" },
];
const LANGS = [
  ["en", "English"], ["hi", "हिन्दी"], ["bn", "বাংলা"], ["ta", "தமிழ்"], ["te", "తెలుగు"], ["mr", "मराठी"], ["gu", "ગુજરાતી"], ["pa", "ਪੰਜਾਬੀ"], ["ur", "اردو"],
  ["es", "Español"], ["fr", "Français"], ["pt", "Português"], ["ar", "العربية"], ["sw", "Kiswahili"], ["zh", "中文"], ["vi", "Tiếng Việt"], ["id", "Bahasa Indonesia"],
];

function copyText(text) {
  if (!text) return;
  try {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  } catch {
    toast.error("Could not copy");
  }
}

function OutputCard({ icon: Icon, label, text, count, testid }) {
  return (
    <div className="rounded-2xl border border-black/10 bg-white p-4 md:p-5" data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon size={16} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />
        <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830]">{label}</div>
        {count && <span className="ml-auto font-mono-tech text-[10px] text-[#8A8479]">{count} chars</span>}
      </div>
      <p className="mt-2 font-serif-display text-base leading-relaxed whitespace-pre-wrap">{text}</p>
      <button
        onClick={() => copyText(text)}
        className="mt-3 inline-flex items-center gap-1 rounded-full border border-black/15 bg-white px-3 py-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5"
        data-testid={`${testid}-copy`}
      >
        <Copy size={11} weight="fill" /> Copy
      </button>
    </div>
  );
}

export default function FarmersMarketing() {
  const { user, openLogin } = useAuth();
  const [form, setForm] = useState({
    topic: "", farm_name: "", crop: "", quantity: "", price: "",
    when: "", location: "", contact: "", tone: "warm", language: "en",
  });
  const [output, setOutput] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!user) return;
    http.get("/farmers/marketing/history?limit=10")
      .then(({ data }) => setHistory(data.runs || []))
      .catch(() => {});
  }, [user, output]);

  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (form.topic.trim().length < 3) {
      toast.error("Tell me what you have — one line is enough.");
      return;
    }
    setBusy(true);
    setOutput(null);
    try {
      const { data } = await http.post("/farmers/marketing/generate", form);
      setOutput(data.output);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Something went wrong";
      toast.error(String(msg));
    } finally {
      setBusy(false);
    }
  };

  const reuse = (run) => {
    setForm(run.input);
    setOutput(run.output);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <main className="bg-[color:var(--np-bg,#F5EBDC)] text-[#151312] min-h-screen pt-32 md:pt-36 pb-32" data-testid="farmers-marketing-page">
      <SEOHead
        title="AI Marketing Assistant · FarmersPulse"
        description="One line about what you have. Five ready-to-post promotions — Facebook, Instagram, WhatsApp, SMS and a printable flyer. Built for working farmers."
        path="/marketing"
      />
      <FarmersNav />

      <div className="max-w-[1080px] mx-auto px-5 md:px-8 mt-6">
        <Link to={fpPath("/toolkit")} className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830] hover:text-[#151312]" data-testid="marketing-back">
          <ArrowLeft size={11} weight="bold" /> Back to toolkit
        </Link>

        <header className="mt-3">
          <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-2">
            <Sparkle size={12} weight="fill" /> Marketing Suite · Tool 1
          </div>
          <h1 className="mt-2 font-serif-display text-4xl md:text-5xl leading-[0.98] tracking-tight">
            One line in. Five channels out.
          </h1>
          <p className="mt-2 font-serif-display italic text-lg text-[#3A3830]">
            Tell me what you have and when. I'll draft the Facebook post, Instagram caption, WhatsApp blast, SMS and a printable flyer — in your language, in your voice.
          </p>
        </header>

        {/* Form */}
        <form onSubmit={submit} className="mt-6 rounded-3xl border border-black/10 bg-white p-6 md:p-8 space-y-4" data-testid="marketing-form">
          <label className="block">
            <div className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830] mb-1">What do you have? · required</div>
            <textarea
              value={form.topic}
              onChange={(e) => upd("topic", e.target.value)}
              placeholder="e.g. 50 kg vine-ripened tomatoes ready for pickup Saturday morning at the farm gate"
              rows={3}
              maxLength={800}
              className="w-full rounded-2xl border border-black/15 bg-white p-3 text-base"
              data-testid="marketing-topic"
              required
            />
          </label>

          <div className="grid md:grid-cols-2 gap-3">
            <label>
              <div className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830] mb-1">Farm name</div>
              <input value={form.farm_name} onChange={(e) => upd("farm_name", e.target.value)} className="w-full rounded-full border border-black/15 bg-white px-4 py-2 text-sm" data-testid="marketing-farm-name" placeholder="Green Valley Farm" />
            </label>
            <label>
              <div className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830] mb-1">Crop</div>
              <input value={form.crop} onChange={(e) => upd("crop", e.target.value)} className="w-full rounded-full border border-black/15 bg-white px-4 py-2 text-sm" data-testid="marketing-crop" placeholder="Tomatoes / Rice / Coffee" />
            </label>
            <label>
              <div className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830] mb-1">Quantity</div>
              <input value={form.quantity} onChange={(e) => upd("quantity", e.target.value)} className="w-full rounded-full border border-black/15 bg-white px-4 py-2 text-sm" data-testid="marketing-quantity" placeholder="50 kg / 20 crates" />
            </label>
            <label>
              <div className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830] mb-1">Price</div>
              <input value={form.price} onChange={(e) => upd("price", e.target.value)} className="w-full rounded-full border border-black/15 bg-white px-4 py-2 text-sm" data-testid="marketing-price" placeholder="₹40/kg · $3/lb" />
            </label>
            <label>
              <div className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830] mb-1">When (pickup / harvest / delivery)</div>
              <input value={form.when} onChange={(e) => upd("when", e.target.value)} className="w-full rounded-full border border-black/15 bg-white px-4 py-2 text-sm" data-testid="marketing-when" placeholder="Saturday 7am-noon" />
            </label>
            <label>
              <div className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830] mb-1">Location</div>
              <input value={form.location} onChange={(e) => upd("location", e.target.value)} className="w-full rounded-full border border-black/15 bg-white px-4 py-2 text-sm" data-testid="marketing-location" placeholder="Farm gate · Village · Farmers market stall #4" />
            </label>
            <label>
              <div className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830] mb-1">Contact</div>
              <input value={form.contact} onChange={(e) => upd("contact", e.target.value)} className="w-full rounded-full border border-black/15 bg-white px-4 py-2 text-sm" data-testid="marketing-contact" placeholder="WhatsApp / phone / website" />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label>
                <div className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830] mb-1">Tone</div>
                <select value={form.tone} onChange={(e) => upd("tone", e.target.value)} className="w-full rounded-full border border-black/15 bg-white px-3 py-2 text-sm" data-testid="marketing-tone">
                  {TONES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </label>
              <label>
                <div className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830] mb-1">Language</div>
                <select value={form.language} onChange={(e) => upd("language", e.target.value)} className="w-full rounded-full border border-black/15 bg-white px-3 py-2 text-sm" data-testid="marketing-language">
                  {LANGS.map(([c, l]) => <option key={c} value={c}>{l}</option>)}
                </select>
              </label>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap pt-2">
            <button
              type="submit"
              disabled={busy || form.topic.trim().length < 3}
              data-testid="marketing-submit"
              className="rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90 disabled:opacity-40 inline-flex items-center gap-2"
            >
              {busy ? <ArrowClockwise size={12} weight="fill" className="animate-spin" /> : <PaperPlaneTilt size={12} weight="fill" />}
              {busy ? "Drafting…" : "Draft my promotions"}
            </button>
            {!user && (
              <button type="button" onClick={() => openLogin("marketing")} className="rounded-full border border-black/15 bg-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase" data-testid="marketing-login">
                Sign in to save your drafts
              </button>
            )}
          </div>
        </form>

        {/* Output */}
        {output && (
          <section className="mt-8 space-y-4" data-testid="marketing-output">
            <h2 className="font-serif-display text-3xl">Your promotions</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <OutputCard icon={FacebookLogo} label="Facebook" text={output.facebook} count={(output.facebook || "").length} testid="marketing-out-facebook" />
              <OutputCard icon={InstagramLogo} label="Instagram" text={output.instagram} count={(output.instagram || "").length} testid="marketing-out-instagram" />
              <OutputCard icon={WhatsappLogo} label="WhatsApp" text={output.whatsapp} count={(output.whatsapp || "").length} testid="marketing-out-whatsapp" />
              <OutputCard icon={ChatCircle} label="SMS" text={output.sms} count={(output.sms || "").length} testid="marketing-out-sms" />
            </div>

            {output.flyer && (
              <div className="rounded-3xl border border-[color:var(--np-brand,#B85F2A)]/40 bg-[#FBF2E1] p-6 md:p-8" data-testid="marketing-out-flyer">
                <div className="flex items-center gap-2 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                  <FileText size={14} weight="fill" /> Printable flyer
                </div>
                <h3 className="mt-2 font-serif-display text-4xl md:text-5xl leading-[0.98]">{output.flyer.title}</h3>
                {output.flyer.subtitle && (
                  <p className="mt-2 font-serif-display italic text-lg text-[#3A3830]">{output.flyer.subtitle}</p>
                )}
                {Array.isArray(output.flyer.bullets) && (
                  <ul className="mt-4 space-y-1.5">
                    {output.flyer.bullets.map((b, i) => (
                      <li key={i} className="font-serif-display text-base leading-relaxed before:content-['·'] before:mr-2 before:text-[color:var(--np-brand,#B85F2A)]">{b}</li>
                    ))}
                  </ul>
                )}
                {output.flyer.cta && (
                  <div className="mt-5 inline-block rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-5 py-2 font-mono-tech text-[11px] tracking-[0.32em] uppercase">
                    {output.flyer.cta}
                  </div>
                )}
                <button onClick={() => window.print()} className="ml-3 rounded-full border border-black/15 bg-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5" data-testid="marketing-flyer-print">
                  Print flyer
                </button>
              </div>
            )}
          </section>
        )}

        {/* History */}
        {user && history.length > 0 && (
          <section className="mt-10" data-testid="marketing-history">
            <h2 className="font-serif-display text-2xl">Your recent drafts</h2>
            <ul className="mt-3 space-y-2">
              {history.slice(0, 10).map((r) => (
                <li key={r.id} className="rounded-2xl border border-black/10 bg-white p-3 md:p-4 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830]">
                      {new Date(r.created_at).toLocaleString()} · {r.language} · {r.tone}
                    </div>
                    <div className="mt-1 text-sm truncate">{(r.input || {}).topic}</div>
                  </div>
                  <button onClick={() => reuse(r)} className="rounded-full border border-black/15 bg-white px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5" data-testid={`marketing-history-reuse-${r.id}`}>
                    Reuse
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </main>
  );
}
